import { FC, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import type {
  Decision,
  DecisionPayload,
  FileCandidate,
  Proposal,
} from 'src/services/DataVaultTransfer.service';
import TransferScalarProposal from './TransferScalarProposal';
import TransferFileProposal from './TransferFileProposal';
import { decisionKey } from '../types';

interface TransferMultiGroupProposalProps {
  groupLabel: string;
  instanceIndex: number;
  proposals: Proposal[]; // all proposals for this (group, instance)
  decisions: Map<string, Decision>;
  onSave: (payload: DecisionPayload) => void;
}

function isProposalDiscarded(
  proposal: Proposal,
  decision: Decision | undefined
): boolean {
  if (!decision) {
    return false;
  }
  if (proposal.kind === 'file') {
    // File is discarded when decision is 'files_selected' with empty payload
    return (
      decision.decision === 'files_selected' &&
      (!decision.files_payload ||
        (Array.isArray(decision.files_payload) &&
          (decision.files_payload as unknown[]).length === 0))
    );
  }
  return decision.decision === 'discard';
}

function buildAllFilesPayload(files: FileCandidate[]): unknown {
  return files.map((file) => ({
    included: true,
    file_hash: file.file_hash,
    source_survey_file_id: file.source_survey_file_id,
    children: file.children.map((child) => ({
      included: true,
      child_qit_id: child.child_qit_id,
      source_survey_answer_id: child.source_survey_answer_id,
    })),
  }));
}

const TransferMultiGroupProposal: FC<TransferMultiGroupProposalProps> = ({
  groupLabel,
  instanceIndex,
  proposals,
  decisions,
  onSave,
}) => {
  const allDiscarded = useMemo(() => {
    return proposals.every((p) => {
      const decision = decisions.get(decisionKey(p.qit_id, p.instance_index));
      return isProposalDiscarded(p, decision);
    });
  }, [proposals, decisions]);

  const handleDiscardInstance = useCallback(() => {
    proposals.forEach((p) => {
      if (p.kind === 'file') {
        onSave({
          qit_id: p.qit_id,
          instance_index: p.instance_index,
          decision: 'files_selected',
          files_payload: [],
        });
      } else {
        onSave({
          qit_id: p.qit_id,
          instance_index: p.instance_index,
          decision: 'discard',
        });
      }
    });
  }, [proposals, onSave]);

  const handleConfirmInstance = useCallback(() => {
    proposals.forEach((p) => {
      if (p.kind === 'file') {
        onSave({
          qit_id: p.qit_id,
          instance_index: p.instance_index,
          decision: 'files_selected',
          files_payload: buildAllFilesPayload(p.files ?? []),
        });
      } else {
        const source = p.candidates?.[0]?.sources[0];
        onSave({
          qit_id: p.qit_id,
          instance_index: p.instance_index,
          decision: 'confirm',
          chosen_source_survey_id: source?.survey_id ?? null,
          chosen_source_qit_id: source?.qit_id ?? null,
          chosen_source_answer_group_id: source?.answer_group_id ?? null,
        });
      }
    });
  }, [proposals, onSave]);

  return (
    <Box>
      {/* Group sub-header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pl: 5.25,
          py: 1,
          bgcolor: 'grey.100',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
        >
          {groupLabel} — Entry #{instanceIndex + 1}
        </Typography>
        {allDiscarded ? (
          <Typography
            variant="caption"
            onClick={handleConfirmInstance}
            sx={{
              color: 'success.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '0.6875rem',
              '&:hover': { color: 'success.dark' },
            }}
          >
            Confirm all
          </Typography>
        ) : (
          <Typography
            variant="caption"
            onClick={handleDiscardInstance}
            sx={{
              color: 'error.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '0.6875rem',
              '&:hover': { color: 'error.dark' },
            }}
          >
            Discard all
          </Typography>
        )}
      </Box>

      {/* Group proposals — rendered as rows */}
      {proposals.map((proposal) => {
        const key = `${proposal.qit_id}_${proposal.instance_index}_${proposal.kind}`;
        const decision = decisions.get(
          decisionKey(proposal.qit_id, proposal.instance_index)
        );

        if (proposal.kind === 'file') {
          return (
            <TransferFileProposal
              key={key}
              proposal={proposal}
              decision={decision}
              onSave={onSave}
            />
          );
        }

        return (
          <TransferScalarProposal
            key={key}
            proposal={proposal}
            decision={decision}
            onSave={onSave}
          />
        );
      })}
    </Box>
  );
};

export default TransferMultiGroupProposal;
